import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { SEMANTIC_BUCKETS } from "@/lib/statusSemantics";
import { TierBadge } from "@/components/presentation/TierBadge";
import { isDemoUser } from "@/lib/demoMode";
import { useAuth } from "@/context/AuthContext";

// v1.2 Part 2: "Business Pulse" (Admin/Office Staff only) — a calm,
// single operational-health gauge (never a dry KPI grid) paired with a
// ranked "Today's Priorities" list. Score/labels are computed
// deterministically server-side (see insights_engine.py business_pulse
// / todays_priorities) and always explain "why" in plain language.
const scrollToAnchor = (anchor) => {
  window.setTimeout(() => {
    document.querySelector(`[data-anchor="${anchor}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 250);
};

export const BusinessPulse = ({ pulse, priorities, loading }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showTier = isDemoUser(user);

  const handlePriorityClick = (p) => {
    if (!p.next_step) return;
    navigate(p.next_step.route);
    if (p.next_step.anchor) scrollToAnchor(p.next_step.anchor);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5">
          <Skeleton className="h-4 w-28 mb-3" />
          <Skeleton className="h-2 w-full mb-3" />
          <Skeleton className="h-3 w-2/3" />
        </div>
        <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5">
          <Skeleton className="h-4 w-32 mb-3" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (!pulse) return null;
  const sem = SEMANTIC_BUCKETS[pulse.bucketKey] || SEMANTIC_BUCKETS.good_completed;

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5" data-testid="business-pulse-card">
        <div className="flex items-center justify-between gap-2">
          <p className="font-heading text-sm text-muted-foreground uppercase tracking-wide">Business Pulse</p>
          {showTier && <TierBadge tier="professional" size="xs" />}
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          <Badge className={`text-xs border ${sem.badge}`} data-testid="business-pulse-status">{pulse.status_label}</Badge>
          <span className="text-xs text-muted-foreground font-mono-ui">{pulse.score}/100</span>
        </div>
        <Progress value={pulse.score} className="h-2 mt-3" />
        <p className="mt-2 text-sm text-muted-foreground" data-testid="business-pulse-explanation">{pulse.explanation}</p>
      </div>

      <div className="rounded-[var(--radius)] border bg-card p-4 sm:p-5" data-testid="todays-priorities-card">
        <p className="font-heading text-sm text-muted-foreground uppercase tracking-wide mb-1">Today&apos;s Priorities</p>
        {(!priorities || priorities.length === 0) ? (
          <p className="text-sm text-muted-foreground py-3" data-testid="todays-priorities-empty-state">
            Great news — nothing urgent needs attention today. A good moment to follow up with a prospective
            student or get ahead on next week's planning.
          </p>
        ) : (
          <div className="divide-y">
            {priorities.map((p) => {
              const psem = SEMANTIC_BUCKETS[p.bucketKey] || SEMANTIC_BUCKETS.waiting;
              return (
                <button
                  key={p.id}
                  onClick={() => handlePriorityClick(p)}
                  className="group flex items-start justify-between gap-3 rounded-md px-1 py-3 w-full text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--role-accent))] transition-colors"
                  data-testid="todays-priorities-row"
                >
                  <div className="flex items-start gap-2 min-w-0">
                    <span className="font-mono-ui text-xs text-muted-foreground mt-0.5 shrink-0">{p.rank}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium flex items-center gap-1.5">
                        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${psem.dot}`} />
                        {p.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.why}</p>
                    </div>
                  </div>
                  {p.next_step && (
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
